import { useEffect } from "react";

/**
 * Set per-page SEO tags directly on document.head.
 *
 * Why not react-helmet-async: it is a dependency here and it is used, but its
 * tags do not reliably reach the document in this app - verified on production,
 * where brand pages render live data yet document.title stays the index.html
 * default, canonical is absent and no JSON-LD is emitted. (The same failure hid
 * the GTM/gtag snippet, which analyticsService now injects itself.) Until that
 * is untangled, SEO tags are written by
 * hand so they are actually present.
 *
 * Every tag created here is marked data-seo-managed and removed on unmount, so
 * one route never leaks its metadata into the next.
 */

const MANAGED = "data-seo-managed";

const upsert = (selector, create, apply) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    el.setAttribute(MANAGED, "true");
    document.head.appendChild(el);
  }
  apply(el);
  return el;
};

export const useSeo = ({ title, description, canonical, jsonLd, image }) => {
  useEffect(() => {
    const previousTitle = document.title;
    if (title) document.title = title;

    if (description) {
      upsert(
        'meta[name="description"]',
        () => Object.assign(document.createElement("meta"), { name: "description" }),
        (el) => el.setAttribute("content", description)
      );
    }

    if (canonical) {
      upsert(
        'link[rel="canonical"]',
        () => Object.assign(document.createElement("link"), { rel: "canonical" }),
        (el) => el.setAttribute("href", canonical)
      );
    }

    const social = [
      ["property", "og:type", "website"],
      ["property", "og:title", title],
      ["property", "og:description", description],
      ["property", "og:url", canonical],
      ["property", "og:image", image],
      ["name", "twitter:card", image ? "summary_large_image" : "summary"],
      ["name", "twitter:title", title],
      ["name", "twitter:description", description],
      ["name", "twitter:image", image],
    ];

    social.forEach(([attr, key, value]) => {
      if (!value) return;
      upsert(
        `meta[${attr}="${key}"]`,
        () => {
          const el = document.createElement("meta");
          el.setAttribute(attr, key);
          return el;
        },
        (el) => el.setAttribute("content", value)
      );
    });

    let ldEl = null;
    if (jsonLd) {
      // The prerendered HTML carries the same graph for the first response
      // (scripts/prerender-meta.js); the page's own copy replaces it so the
      // document never holds two.
      document.head
        .querySelectorAll('script[type="application/ld+json"][data-prerendered]')
        .forEach((el) => el.remove());
      ldEl = document.createElement("script");
      ldEl.type = "application/ld+json";
      ldEl.setAttribute(MANAGED, "true");
      ldEl.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(ldEl);
    }

    return () => {
      document.title = previousTitle;
      if (ldEl && ldEl.parentNode) ldEl.parentNode.removeChild(ldEl);
      document.head
        .querySelectorAll(`link[rel="canonical"][${MANAGED}]`)
        .forEach((el) => el.remove());
    };
  }, [title, description, canonical, image, jsonLd]);
};

export default useSeo;
